// src/components/IntroEnemy.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Seo from '../seo/Seo';
import styles from '../css/modules/IntroEnemy.module.css';
import EnergyBar from './EnergyBar';
import Typewriter from './Typewriter';
import AnalogCounter from './AnalogCounter';
import { useUser } from './UserContext';
import { FiRefresh } from './icons';
import { LICENCJE_DO_WYBORU, wybranaLicencja, zapiszLicencje } from '../lib/licencjaWyboru';
import { processImagePath } from '../lib/format';

/* Prerender (headless Chrome uderzający w lokalny serwer) nigdy nie dostanie
   danych przeciwnika – API odrzuca żądanie z origin http://localhost.
   Bez tego w build/intro/index.html zapisałby się komunikat o błędzie
   i każdy użytkownik widziałby go przez ułamek sekundy przed startem aplikacji. */
const isPrerender =
  typeof navigator !== 'undefined' && /HeadlessChrome/i.test(navigator.userAgent);

const IntroEnemy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  // Wybór licencji decyduje o puli pytań w pojedynku. Trzymamy go w
  // localStorage (patrz src/lib/licencjaWyboru.js), bo czyta go także skrypt
  // startujący pobieranie pytań jeszcze przed uruchomieniem aplikacji.
  const [licencja, setLicencja] = useState(wybranaLicencja);

  const handleLicencja = slug => {
    setLicencja(slug);
    zapiszLicencje(slug);
  };

  const defaultUserData = {
    image: 'my-photo',
    name: 'Pilot',
    energy: 100,
    score: 550,
    ranking: 0,
    level: 0,
    artefacts: [],
  };

  const passedUserData = location.state?.userData;
  const [userData, setUserData] = useState(user || passedUserData || defaultUserData);

  // Stan przechowujący aktualnie wybranego przeciwnika i listę przeciwników pobranych z API
  const [enemy, setEnemy] = useState(null);
  const [enemies, setEnemies] = useState([]);
  const [enemyIndex, setEnemyIndex] = useState(0);
  const [loadingEnemy, setLoadingEnemy] = useState(true);
  const [errorEnemy, setErrorEnemy] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const WEBSITE = process.env.REACT_APP_WEBSITE;

  // 🔁 Funkcja do pobierania listy przeciwników z nowego endpointu
  const fetchEnemiesData = useCallback(async () => {
    setLoadingEnemy(true);
    setErrorEnemy(null);
    try {
      /* Żądanie startuje już w <head> (public/index.html), zanim przeglądarka
         pobierze i wykona bundle – oszczędza ok. pół sekundy oczekiwania na
         przycisk START. Tu tylko odbieramy gotową obietnicę; kolejne
         losowania przeciwnika idą normalną ścieżką. */
      const pending = window.__enemyRequest;
      window.__enemyRequest = null;

      const url = `${API_BASE_URL}/cyber/get_random_enemies?website=${WEBSITE}`;
      const response = await (pending ||
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }));

      if (!response.ok) {
        throw new Error('Błąd sieciowy: nie udało się pobrać danych przeciwników');
      }

      // Zakładamy, że metoda zwraca tablicę przeciwników (np. { enemies: [...] })
      const data = await response.json();
      // Jeśli backend zwraca listę przeciwników w kluczu "enemies",
      // w przeciwnym wypadku należy odpowiednio dostosować, np. const enemyList = data;
      const enemyList = data.enemies || data;
      if (!Array.isArray(enemyList) || enemyList.length === 0) {
        throw new Error('Brak przeciwników do wyświetlenia');
      }
      setEnemies(enemyList);
      setEnemy(enemyList[0]);
      setEnemyIndex(0);
    } catch (err) {
      console.error('Błąd podczas pobierania danych przeciwników:', err);
      setErrorEnemy(err.message);
    } finally {
      setLoadingEnemy(false);
    }
  }, [API_BASE_URL, WEBSITE]);

  // Pierwsze pobranie listy przeciwników
  useEffect(() => {
    fetchEnemiesData();
  }, [fetchEnemiesData]);

  /* Rozgrzewamy paczkę quizu, gdy użytkownik jest już na ekranie startowym –
     kliknie WALCZ/START za chwilę, a paczka jest wtedy w cache. Wcześniej
     robił to webpackPrefetch przy imporcie trasy, ale ten dokładał ~50 kB
     pobierania do KAŻDEJ podstrony (także cennika i poradników).
     W headless Chrome pomijamy: inaczej prerender uznałby tę paczkę za
     część strony i dokleiłby jej <link rel="preload">. */
  useEffect(() => {
    if (typeof navigator !== 'undefined' && /HeadlessChrome/i.test(navigator.userAgent)) return undefined;
    const timer = setTimeout(() => {
      import('./QuizCyberpunkSingle').catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user) {
      setUserData(user);
    }
  }, [user]);

  useEffect(() => {
    const updateEnergy = async () => {
      /* Dolewka rowna pelnej energii startowej (100), nie 10.
         Sila przeciwnikow to 5-20, a kazda bledna odpowiedz zabiera cala sile,
         wiec przy 10 pierwsza pomylka konczyla pojedynek - gracz ZALOGOWANY mial
         gorzej niz anonimowy, ktory startuje ze 100 (patrz defaultUserData wyzej). */
      if (userData && userData.energy < 1) {
        const updatedUserData = { ...userData, energy: 100 };
        setUserData(updatedUserData);
        const updatedData = { energy: 100 };
        try {
          const token = localStorage.getItem('access_token');
          await fetch(`${API_BASE_URL}/cyber/user_info`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(updatedData),
          });
        } catch (error) {
          console.error('Error updating user info in DB:', error);
        }
      }
    };
    updateEnergy();
  }, [userData, API_BASE_URL]);

  const handleContinue = () => {
    if (enemy) {
      navigate(`/quiz/${enemy.slug}`, { state: { enemyData: enemy, userData } });
    }
  };

  const handleShop = () => {
    navigate('/shop', { state: { score: userData.score } });
  };

  // Funkcja obsługująca kliknięcie - przechodzi do kolejnego przeciwnika z listy lub pobiera nową listę
  const handleEnemyClick = () => {
    const nextIndex = enemyIndex + 1;
    if (nextIndex < enemies.length) {
      setEnemy(enemies[nextIndex]);
      setEnemyIndex(nextIndex);
    } else {
      // Skończyliśmy aktualną listę, pobieramy nową
      fetchEnemiesData();
    }
  };

  /* Ekran ma zawsze ten sam układ – także zanim dojdą dane przeciwnika.
     Wcześniej w tym miejscu było gołe „Ładowanie danych…" (albo komunikat
     błędu), więc strona dwa razy przeskakiwała, a prerender nie miał czego
     zapisać. Teraz pierwszy paint to od razu właściwa arena z placeholderem. */
  const enemyReady = Boolean(enemy);
  const showError = Boolean(errorEnemy) && !isPrerender;

  return (
    <div className={styles.cyberpunkContainer}>
      <Seo path="/intro" />

      <header className={styles.introHeader}>
        <h1>Darmowy test dla pilotów</h1>
        <p>
          Wybierz uprawnienie, zmierz się z przeciwnikiem i odpowiedz na 10 pytań z zakresu
          egzaminu teoretycznego. Poprawna odpowiedź odbiera przeciwnikowi punkty życia, błędna
          kosztuje Twoją energię, a na każde pytanie masz ograniczony czas. Bez rejestracji
          i bez opłat.
        </p>
        <div className={styles.licencjaWybor} role="group" aria-label="Wybierz uprawnienie">
          {LICENCJE_DO_WYBORU.map(l => (
            <button
              key={l.slug}
              type="button"
              className={l.slug === licencja ? styles.licencjaAktywna : styles.licencjaPrzycisk}
              onClick={() => handleLicencja(l.slug)}
              aria-pressed={l.slug === licencja}
            >
              {l.nazwa}
            </button>
          ))}
        </div>
      </header>

      {showError && (
        <p className={styles.loadError}>
          Nie udało się pobrać przeciwnika.{' '}
          <button type="button" className={styles.retryButton} onClick={fetchEnemiesData}>
            Spróbuj ponownie
          </button>
        </p>
      )}

      <div className={styles.fightContainer}>
        <div className={styles.enemyBox}>
          {/* Zmianę przeciwnika obsługuje wyłącznie przycisk obok - obrazek
              z onClick był niefokusowalny i niewidoczny dla czytnika. */}
          <div className={styles.enemyImageWrapper}>
            {enemyReady ? (
              <img
                src={`/enemies/${enemy.slug}.webp`}
                alt={enemy.name}
                className={styles.cyberImage}
              />
            ) : (
              <div className={`${styles.cyberImage} ${styles.imagePlaceholder}`} aria-hidden="true" />
            )}
            <button
              type="button"
              className={styles.refreshIcon}
              onClick={handleEnemyClick}
              title="Zmień przeciwnika"
              disabled={!enemyReady}
              aria-label="Zmień przeciwnika"
            >
              <FiRefresh />
            </button>
          </div>
          <h2 className={styles.enemyName}>
            {enemyReady ? <Typewriter text={enemy.name} typingSpeed={60} /> : 'Przeciwnik'}
          </h2>
          {enemyReady && enemy.motto && <div className={styles.motto}>{enemy.motto}</div>}
          <div className={styles.stats}>
            <div className={styles.statRow}>
              <span className={styles.statLabel}><strong>Trudność:</strong></span>
              <span className={styles.statValue}><AnalogCounter value={(enemy && enemy.strength) || 0} /></span>
            </div>
            <div className={styles.statRow}>
              <span className={styles.statLabel}><strong>Energia:</strong></span>
              <span className={styles.statValue}>
                <EnergyBar
                  current={enemy ? enemy.health : 0}
                  ariaLabel={`Energia przeciwnika: ${enemy ? enemy.health : 0} ze 100`}
                />
              </span>
            </div>
            <div className={styles.statRow}>
              <span className={styles.statLabel}><strong>Czas na pytanie:</strong></span>
              <span className={styles.statValue}><AnalogCounter value={(enemy && enemy.time) || 0} /></span>
            </div>
          </div>
        </div>

        <div className={styles.buttonContainer}>
        <button
          className={styles.startBattleButton}
          onClick={handleContinue}
          disabled={!enemyReady}
          aria-busy={loadingEnemy}
        >
          START
        </button>
      </div>

        <div className={styles.userBox}>
          <img
            src={processImagePath(userData.image)}
            alt={userData.name}
            className={styles.cyberImage}
          />
          <h2 className={styles.userName}>{userData.name}</h2>
          <div className={styles.stats}>
            <div className={styles.statRow}>
              <span className={styles.statLabel}><strong>Punkty:</strong></span>
              <span className={styles.statValue}><AnalogCounter value={userData.score || 0} /></span>
            </div>
            <div className={styles.statRow}>
              <span className={styles.statLabel}><strong>Energia:</strong></span>
              <span className={styles.statValue}>
                <EnergyBar
                  current={userData.energy}
                  ariaLabel={`Twoja energia: ${userData.energy} ze 100`}
                />
              </span>
            </div>
            <div className={styles.statRow}>
              <span className={styles.statLabel}><strong>Poziom:</strong></span>
              <span className={styles.statValue}><AnalogCounter value={userData.level || 0} /></span>
            </div>
            <div className={styles.statRow}>
              <span className={styles.statLabel}><strong>Trafione:</strong></span>
              <span className={styles.statValue}><AnalogCounter value={userData.answered_correct || 0} /></span>
            </div>
          </div>
          <div className={styles.shopButtonContainer}>
            <button className={styles.shopButton} onClick={handleShop}>
              Sklep
            </button>
          </div>
          {userData.artefacts && (
            <div className={styles.artefactsContainer}>
              <div className={styles.artefactsBox}>
                {Array.from({ length: 4 }, (_, index) => {
                  const artifact = userData.artefacts[index];
                  return (
                    <div key={index} className={styles.artifactSlot}>
                      {artifact ? (
                        <img
                          src={`/artefacts/${artifact}.webp`}
                          alt={`Artefakt ${index + 1}`}
                          className={styles.artifactImage}
                        />
                      ) : (
                        <div className={styles.emptyArtifact} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export default IntroEnemy;
